import { Fragment } from 'react';
import { sanitizeUrl } from '@/lib/sanitizeUrl';

interface PlainTextContentProps {
  /** Raw plaintext content — no markup, or markup that didn't match any known lightweight-markup format. */
  content: string;
  className?: string;
}

const URL_RE = /https?:\/\/[^\s<>[\]]+/g;

/**
 * Renders content with no detected markup: preserved whitespace/line
 * breaks, with bare URLs linkified. This is the fallback used by
 * `WikiContent` when `@/lib/contentFormat` can't find any Djot, AsciiDoc,
 * or Markdown signal at all — most wiki content genuinely is just plain
 * prose, and running it through a lightweight-markup parser would be
 * both pointless and risk misinterpreting stray punctuation as syntax.
 *
 * Security: renders exclusively through JSX text interpolation (React
 * auto-escapes) — never `dangerouslySetInnerHTML`. Every linkified URL
 * passes through `sanitizeUrl()`.
 */
export function PlainTextContent({ content, className }: PlainTextContentProps) {
  const parts = content.split(URL_RE);
  const urls = content.match(URL_RE) ?? [];

  return (
    <div className={className}>
      <p className="mb-0 leading-7 whitespace-pre-wrap break-words" dir="auto">
        {parts.map((part, i) => (
          <Fragment key={i}>
            {part}
            {urls[i] && <LinkifiedUrl url={urls[i]} />}
          </Fragment>
        ))}
      </p>
    </div>
  );
}

function LinkifiedUrl({ url }: { url: string }) {
  // Strip common trailing punctuation that's more likely to be sentence
  // punctuation than part of the URL (e.g. "see https://example.com.").
  const trailingMatch = url.match(/[.,;:!?)]+$/);
  const trailing = trailingMatch ? trailingMatch[0] : '';
  const href = sanitizeUrl(trailing ? url.slice(0, -trailing.length) : url);

  if (!href) return <>{url}</>;

  return (
    <>
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-primary underline underline-offset-2 hover:no-underline break-all"
      >
        {trailing ? url.slice(0, -trailing.length) : url}
      </a>
      {trailing}
    </>
  );
}
